import { createTheme, alpha } from '@mui/material/styles';

// Inklantern design tokens.
//
// "Ink" is the near-black violet used for the header and primary text,
// "plum" is the brand purple used for anything interactive (selected
// toggles, links, focus), and "lantern" is a warm amber kept for a single
// job: marking where you are (the active nav tab). Chart series colors come
// from each org's brand/pillar color settings, so the chrome around them
// stays neutral.
export type InkMode = 'light' | 'dark';

export interface InkTokens {
  ink: string;
  plum: string;
  plumSoft: string;
  lantern: string;
  page: string;
  surface: string;
  surfaceAlt: string;
  line: string;
  text: string;
  muted: string;
  positive: string;
  negative: string;
}

export const inkTokens: Record<InkMode, InkTokens> = {
  light: {
    ink: '#1B1526',
    plum: '#86279C',
    plumSoft: '#F4EAF7',
    lantern: '#F0A830',
    page: '#F5F3F7',
    surface: '#FFFFFF',
    surfaceAlt: '#FAF8FB',
    line: '#E4DFEA',
    text: '#1B1526',
    muted: '#6E6679',
    positive: '#1E7F4F',
    negative: '#B4232C',
  },
  dark: {
    ink: '#0F0C14',
    plum: '#C77DDB',
    plumSoft: '#2A1F31',
    lantern: '#F5B84A',
    page: '#141019',
    surface: '#1D1824',
    surfaceAlt: '#221C2A',
    line: '#2E2738',
    text: '#EEEAF3',
    muted: '#A59DB2',
    positive: '#5FD49A',
    negative: '#FF7A82',
  },
};

export const DISPLAY_FONT = '"Bricolage Grotesque", "Instrument Sans", system-ui, sans-serif';
export const UI_FONT = '"Instrument Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

// Radii by level: panels are the outermost surface, nested blocks sit inside
// a panel, controls are inputs/buttons.
export const radii = { panel: 16, nested: 10, control: 8 };

declare module '@mui/material/styles' {
  interface Theme {
    ink: InkTokens;
  }
  interface ThemeOptions {
    ink?: InkTokens;
  }
}

// Create a function that returns a theme based on the mode for the main Inklantern app
const createAppTheme = (mode: InkMode = 'light') => {
  const t = inkTokens[mode];
  const isDark = mode === 'dark';

  return createTheme({
    ink: t,
    palette: {
      mode,
      primary: {
        main: t.plum,
        contrastText: isDark ? '#1B1526' : '#FFFFFF',
      },
      secondary: {
        main: '#f50057',
      },
      success: { main: t.positive },
      error: { main: t.negative },
      warning: { main: t.lantern },
      background: {
        default: t.page,
        paper: t.surface,
      },
      text: {
        primary: t.text,
        secondary: t.muted,
      },
      divider: t.line,
      action: {
        hover: alpha(t.plum, isDark ? 0.12 : 0.06),
        selected: alpha(t.plum, isDark ? 0.2 : 0.1),
      },
    },
    shape: {
      borderRadius: radii.control,
    },
    typography: {
      fontFamily: UI_FONT,
      fontSize: 14,
      h1: { fontFamily: DISPLAY_FONT, fontWeight: 650, letterSpacing: '-0.02em' },
      h2: { fontFamily: DISPLAY_FONT, fontWeight: 650, letterSpacing: '-0.02em' },
      h3: { fontFamily: DISPLAY_FONT, fontWeight: 650, letterSpacing: '-0.015em' },
      h4: { fontFamily: DISPLAY_FONT, fontWeight: 650, fontSize: '2rem', letterSpacing: '-0.015em', lineHeight: 1.15 },
      h5: { fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em', lineHeight: 1.3 },
      h6: { fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '1.05rem', letterSpacing: '-0.005em', lineHeight: 1.35 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem' },
      body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', color: t.muted },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.page,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '::selection': {
            backgroundColor: alpha(t.plum, 0.22),
          },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(t.muted, 0.35),
            borderRadius: 8,
            border: `2px solid ${t.page}`,
          },
          '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          // Chart libraries render SVG text; keep it on the UI face with
          // tabular figures so axis numbers line up.
          '.recharts-wrapper, .recharts-wrapper text': {
            fontFamily: UI_FONT,
            fontVariantNumeric: 'tabular-nums',
          },
          '.recharts-cartesian-axis-tick-value': {
            fill: t.muted,
          },
          '.recharts-default-tooltip': {
            borderRadius: `${radii.nested}px !important`,
            border: `1px solid ${t.line} !important`,
            backgroundColor: `${t.surface} !important`,
            boxShadow: `0 8px 24px ${alpha('#1B1526', isDark ? 0.5 : 0.12)}`,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: t.ink,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          // Resting surfaces are outlined rather than shadowed; only floating
          // layers (menus, popovers) keep a shadow.
          elevation1: { border: `1px solid ${t.line}`, boxShadow: 'none' },
          elevation2: { border: `1px solid ${t.line}`, boxShadow: 'none' },
          elevation3: { border: `1px solid ${t.line}`, boxShadow: 'none' },
          elevation8: {
            border: `1px solid ${t.line}`,
            boxShadow: `0 12px 32px ${alpha('#1B1526', isDark ? 0.55 : 0.14)}`,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radii.panel,
            border: `1px solid ${t.line}`,
            backgroundColor: t.surface,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: radii.nested },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radii.nested,
            border: `1px solid ${t.line}`,
            boxShadow: `0 12px 32px ${alpha('#1B1526', isDark ? 0.55 : 0.14)}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            borderRadius: 6,
            margin: '0 6px',
            '&.Mui-selected': { backgroundColor: alpha(t.plum, isDark ? 0.2 : 0.1) },
            '&.Mui-selected:hover': { backgroundColor: alpha(t.plum, isDark ? 0.26 : 0.14) },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radii.panel },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radii.control, paddingInline: 14 },
          outlined: { borderColor: t.line },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: radii.control },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? t.surfaceAlt : t.page,
            borderRadius: radii.control,
            padding: 3,
            gap: 2,
            border: `1px solid ${t.line}`,
          },
          grouped: {
            border: 0,
            borderRadius: `${radii.control - 2}px !important`,
            margin: 0,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            lineHeight: 1.2,
            padding: '6px 12px',
            color: t.muted,
            border: 0,
            '&:hover': { backgroundColor: alpha(t.plum, isDark ? 0.14 : 0.06), color: t.text },
            '&.Mui-selected': {
              backgroundColor: t.surface,
              color: t.plum,
              boxShadow: `0 1px 2px ${alpha('#1B1526', isDark ? 0.5 : 0.12)}, 0 0 0 1px ${t.line}`,
            },
            '&.Mui-selected:hover': { backgroundColor: t.surface },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radii.control,
            backgroundColor: t.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(t.muted, 0.6) },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.plum, borderWidth: 1.5 },
          },
        },
      },
      MuiTooltip: {
        // Non-interactive: the pointer passes straight through a tooltip, so
        // resting the mouse on one never blocks scrolling the page.
        defaultProps: { arrow: true, disableInteractive: true },
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#2E2738' : t.ink,
            color: '#F4F1F7',
            fontSize: '0.8rem',
            lineHeight: 1.45,
            fontWeight: 400,
            padding: '8px 12px',
            borderRadius: 8,
            maxWidth: 340,
          },
          arrow: { color: isDark ? '#2E2738' : t.ink },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.line}`,
            fontVariantNumeric: 'tabular-nums',
          },
          head: {
            color: t.muted,
            fontWeight: 600,
            fontSize: '0.8125rem',
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': { backgroundColor: alpha(t.plum, isDark ? 0.08 : 0.035) },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
          sizeSmall: { fontSize: '0.75rem', height: 22 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: { color: t.plum, fontWeight: 500 },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: t.plum,
              color: isDark ? '#1B1526' : '#FFFFFF',
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          thumb: { boxShadow: 'none' },
        },
      },
    },
  });
};

// Create a function that returns a theme based on the mode for the Watchdog component
const createWatchdogTheme = (mode: 'light' | 'dark' = 'light') => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#FECF2B', // Watchdog yellow color
      },
      background: {
        default: mode === 'dark' ? '#1d1d1b' : '#f8f8f8',
        paper: mode === 'dark' ? '#1d1d1b' : '#f8f8f8',
      },
      text: {
        primary: mode === 'dark' ? '#fff' : '#000',
        secondary: '#FECF2B', // Always yellow for secondary text
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
          },
        },
      },
    },
  });
};

// For backward compatibility, export a dark theme as default
const theme = createAppTheme('dark');

export default theme;
export { createAppTheme, createWatchdogTheme };
